import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  creditManagementService,
  CreditManagementCreateData,
  CreditManagementUpdateData,
  CreditImpactPreviewData
} from '@/services/billing/credit-management.service';
import { toast } from 'sonner';

// Helper function for error handling
function getErrorMessage(error: unknown, defaultMessage: string): string {
  if (error && 
      typeof error === 'object' && 
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object' &&
      'message' in error.response.data &&
      typeof error.response.data.message === 'string') {
    return error.response.data.message;
  }
  return defaultMessage;
}

// Query Keys
export const creditManagementKeys = {
  all: ['credit-management'] as const,
  lists: () => [...creditManagementKeys.all, 'list'] as const,
  list: (productId: string) => [...creditManagementKeys.lists(), productId] as const,
  details: () => [...creditManagementKeys.all, 'detail'] as const,
  detail: (productId: string) => [...creditManagementKeys.details(), productId] as const,
  preview: (productId: string) => [...creditManagementKeys.all, 'preview', productId] as const,
};

export function useCreditManagementSettings(productId: string) {
  return useQuery({
    queryKey: creditManagementKeys.detail(productId),
    queryFn: async () => {
      try {
        return await creditManagementService.getCreditManagementSettings(productId);
      } catch (error) {
        // Handle 404 as "not configured" rather than an error
        if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
          return null; // Return null to indicate no settings configured
        }
        throw error; // Re-throw other errors
      }
    },
    enabled: !!productId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useCreateCreditManagementSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: CreditManagementCreateData }) =>
      creditManagementService.createCreditManagementSettings(productId, data),
    onSuccess: (newSettings, { productId }) => {
      // Update cache with new settings
      queryClient.setQueryData(
        creditManagementKeys.detail(productId), 
        newSettings
      );
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: creditManagementKeys.list(productId) });
      
      toast.success('Credit management settings created successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to create credit management settings'));
    },
  });
}

export function useUpdateCreditManagementSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      productId, 
      data 
    }: { 
      productId: string; 
      data: CreditManagementUpdateData 
    }) =>
      creditManagementService.updateCreditManagementSettings(productId, data),
    onMutate: async ({ productId }) => {
      // Cancel any outgoing refetches to prevent conflicts
      await queryClient.cancelQueries({ queryKey: creditManagementKeys.detail(productId) });
      
      // Snapshot the previous value for rollback
      const previousSettings = queryClient.getQueryData(creditManagementKeys.detail(productId));
      
      // Skip optimistic update to prevent UI bouncing
      // The server responds fast enough that optimistic updates aren't needed
      
      return { previousSettings };
    },
    onSuccess: (updatedSettings, { productId }) => {
      // Update the cache with server response
      queryClient.setQueryData(
        creditManagementKeys.detail(productId), 
        updatedSettings
      );
      
      // No need to invalidate since we're directly updating the cache
      // This prevents extra requests and UI bouncing
      
      toast.success('Credit management settings updated successfully');
    },
    onError: (error: unknown, variables, context) => {
      // Rollback optimistic update
      if (context?.previousSettings) {
        queryClient.setQueryData(
          creditManagementKeys.detail(variables.productId), 
          context.previousSettings
        );
      }
      
      toast.error(getErrorMessage(error, 'Failed to update credit management settings'));
    },
    onSettled: (_data, _error, _variables) => {
      // Skip automatic refetch since we update the cache manually in onSuccess
      // This prevents overwriting the correct data with stale cached data
    },
  });
}

export function usePreviewCreditImpact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      productId, 
      data 
    }: { 
      productId: string; 
      data: CreditImpactPreviewData 
    }) =>
      creditManagementService.previewCreditImpact(productId, data),
    onSuccess: (previewResult, { productId }) => {
      // Cache the preview result temporarily
      queryClient.setQueryData(
        creditManagementKeys.preview(productId), 
        previewResult,
        {
          updatedAt: Date.now(),
        }
      );
      
      // Note: Not showing toast for preview as it's just a preview operation
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to preview credit impact'));
    },
  });
}

// Prefetch Hook for hover/preview scenarios
export function usePrefetchCreditManagementSettings() {
  const queryClient = useQueryClient();

  return (productId: string) => {
    queryClient.prefetchQuery({
      queryKey: creditManagementKeys.detail(productId),
      queryFn: () => creditManagementService.getCreditManagementSettings(productId),
      staleTime: 5 * 60 * 1000,
    });
  };
}

// Utility hook to check if credit management is properly configured
export function useCreditManagementStatus(productId: string) {
  const { data: settings, ...rest } = useCreditManagementSettings(productId);
  
  const isConfigured = !!settings;
  const isActive = settings?.track_credit_usage ?? false;
  const hasAutoTopup = settings?.auto_topup?.enabled ?? false;
  const hasNotifications = settings?.notify_on_low_credits ?? false;
  
  const configurationStatus = {
    isConfigured,
    isActive,
    hasAutoTopup,
    hasNotifications,
    lifecycleMode: settings?.lifecycle_mode,
    isComplete: isConfigured && isActive
  };

  return {
    ...rest,
    data: settings,
    status: configurationStatus
  };
}

// Hook to get cached preview result
export function useCachedCreditImpactPreview(productId: string) {
  return useQuery({
    queryKey: creditManagementKeys.preview(productId),
    queryFn: () => {
      // This will only return cached data, not fetch from server
      throw new Error('Preview data should only come from cache');
    },
    enabled: false, // Never automatically fetch
    staleTime: 2 * 60 * 1000, // Preview results are valid for 2 minutes
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
}